import React, { useState } from 'react';
import './Steps.css';

const Step3 = ({ bookingData, updateBookingData, nextStep, prevStep }) => {
  const [selectedWeeks, setSelectedWeeks] = useState(bookingData.selectedWeeks || []);

  const isPublicGroup = bookingData.lessonType === 'group';

  // Hourly pricing based on number of participants
  const getHourlyPrice = (participants) => {
    if (participants === 1) return 170;
    if (participants === 2) return 150;
    if (participants === 3) return 125;
    if (participants >= 4) return 105;
    return 170; // default
  };

  // Calculate Ski Day price based on participants
  const getSkiDayPrice = (participants) => {
    if (participants === 1) return 700;
    if (participants >= 2) return 500;
    return 700; // default
  };

  const packages = [
    {
      id: 'single',
      icon: '🎿',
      title: 'Lecție Unică',
      description: 'O singură oră de schi',
      getPricePerPerson: (participants) => getHourlyPrice(participants),
      duration: '1 oră'
    },
    {
      id: 'multiple',
      icon: '📚',
      title: 'Lecții Multiple',
      description: 'Multiple ore de schi (alegeți câte doriți)',
      getPricePerPerson: (participants) => getHourlyPrice(participants),
      duration: '1 oră/lecție'
    },
    {
      id: 'skiday',
      icon: '⛷️',
      title: 'Pachet Ski Day',
      description: '5-6 ore de schi + echipament complet inclus',
      getPricePerPerson: (participants) => getSkiDayPrice(participants),
      duration: '5-6 ore',
      badge: 'Echipament inclus'
    }
  ];

  const handleSelectPackage = (packageId) => {
    updateBookingData('packageType', packageId);
    
    // Reset weeks selection for non-weekly packages
    if (packageId !== 'weekly') {
      setSelectedWeeks([]);
      updateBookingData('selectedWeeks', []);
    }
    
    // Set default quantity to 3 for multiple lessons
    if (packageId === 'multiple') {
      if (!bookingData.packageQuantity || bookingData.packageType !== 'multiple') {
        updateBookingData('packageQuantity', 3);
      }
    }

    // For skiday, default to 1 day
    if (packageId === 'skiday' && !bookingData.packageQuantity) {
      updateBookingData('packageQuantity', 1);
    }
  };

  const handleNext = () => {
    if (bookingData.packageType === 'weekly') {
      if (selectedWeeks.length > 0) {
        nextStep();
      }
    } else if (bookingData.packageType) {
      nextStep();
    }
  };

  const numberOfParticipants = bookingData.numberOfParticipants || 1;

  // Get current price display
  const getCurrentPackage = () => {
    return packages.find(pkg => pkg.id === bookingData.packageType);
  };

  const getCurrentPricePerPerson = () => {
    const pkg = getCurrentPackage();
    if (pkg && pkg.getPricePerPerson) {
      return pkg.getPricePerPerson(numberOfParticipants);
    }
    return 0;
  };
  const getTotalPriceText = () => {
    if (isPublicGroup) {
      return 'Prețul va fi confirmat telefonic';
    }
    const total = getCurrentPricePerPerson() * (bookingData.packageQuantity || 3);
    return `${total} RON/persoană`;
  };

  return (
    <div className="step-container">
      <h2 className="step-heading">Alegeți tipul de pachet</h2>
      <p className="step-description">
        Pentru {numberOfParticipants} {numberOfParticipants === 1 ? 'persoană' : 'persoane'}
        {isPublicGroup && ' - Lecții în grup public'}
      </p>
      <p className="step-info-note">
        ℹ️ O lecție durează 50 de minute.
      </p>

      {isPublicGroup && (
        <div className="group-pricing-reminder">
          ⚠️ Pentru lecțiile în grup public, prețul final va fi stabilit în funcție de numărul total de participanți și va fi confirmat telefonic.
        </div>
      )}

      <div className="packages-grid">
        {packages.map(pkg => {
          const pricePerPerson = pkg.getPricePerPerson(numberOfParticipants);
          
          return (
            <div
              key={pkg.id}
              className={`package-card ${bookingData.packageType === pkg.id ? 'selected' : ''}`}
              onClick={() => handleSelectPackage(pkg.id)}
            >
              <div className="package-icon">{pkg.icon}</div>
              <h3 className="package-title">{pkg.title}</h3>
              <p className="package-description">{pkg.description}</p>
              {pkg.badge && (
                <div className="package-badge">{pkg.badge}</div>
              )}
             <div className="package-price">
  {isPublicGroup && pkg.id !== 'skiday' ? 'Preț variabil' : `${pricePerPerson} RON/pers`}
</div>
              {bookingData.packageType === pkg.id && (
                <div className="selected-badge">✓ Selectat</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Quantity selector for multiple lessons */}
      {bookingData.packageType === 'multiple' && (
        <div className="package-quantity-container">
          <label>Număr de ore:</label>
          <div className="quantity-selector">
            <button 
              className="qty-btn"
              onClick={() => updateBookingData('packageQuantity', Math.max(1, bookingData.packageQuantity - 1))}
            >
              -
            </button>
            <span className="qty-display">{bookingData.packageQuantity || 3}</span>
            <button 
              className="qty-btn"
              onClick={() => updateBookingData('packageQuantity', (bookingData.packageQuantity || 3) + 1)}
            >
              +
            </button>
          </div>
          {isPublicGroup ? (
            <p className="package-note">
              📞 Prețul total pentru {bookingData.packageQuantity || 3} ore va fi confirmat telefonic
            </p>
          ) : (
            <p className="package-note">
              Preț: {getCurrentPricePerPerson()} RON/oră/persoană × {bookingData.packageQuantity || 3} ore = {getTotalPriceText()}
            </p>
          )}
        </div>
      )}

      {/* Quantity selector for Ski Day package */}
      {bookingData.packageType === 'skiday' && (
        <div className="package-quantity-container">
          <label>Număr de zile:</label>
          <div className="quantity-selector">
            <button 
              className="qty-btn"
              onClick={() => updateBookingData('packageQuantity', Math.max(1, bookingData.packageQuantity - 1))}
            >
              -
            </button>
            <span className="qty-display">{bookingData.packageQuantity || 1}</span>
            <button 
              className="qty-btn"
              onClick={() => updateBookingData('packageQuantity', (bookingData.packageQuantity || 1) + 1)}
            >
              +
            </button>
          </div>
          <p className="package-note">
            Include: 5-6 ore de schi + echipament complet
          </p>
          {isPublicGroup ? (
            <p className="package-note">
              📞 Prețul total pentru {bookingData.packageQuantity || 1} {(bookingData.packageQuantity || 1) === 1 ? 'zi' : 'zile'} va fi confirmat telefonic
            </p>
          ) : (
            <p className="package-note">
            Total: {getCurrentPricePerPerson()} RON/persoană × {bookingData.packageQuantity || 1} {(bookingData.packageQuantity || 1) === 1 ? 'zi' : 'zile'} = {getCurrentPricePerPerson() * (bookingData.packageQuantity || 1)} RON/persoană
            </p>
          )}
        </div>
      )}

      <div className="step-actions">
        <button className="btn-secondary" onClick={prevStep}>
          ← Înapoi
        </button>
        <button 
          className="btn-primary"
          onClick={handleNext}
          disabled={
            !bookingData.packageType ||
            (bookingData.packageType === 'weekly' && selectedWeeks.length === 0)
          }
        >
          Continuă →
        </button>
      </div>
    </div>
  );
};

export default Step3;